<template>
  <v-page>
    <toolbar fixed>
      <template v-slot:left>
        <span class="v-toolbar__text">Notifications</span>
      </template>
    </toolbar>

    <div
      v-if="!isPushEnabled() && isSupportedDevice()"
      class="container px-2 mt-4 pb-1 text-center"
    >
      <v-button
        primary
        block
        :is-loading="isLoadingPush"
        @click="requestNotifications"
      >
        <v-icon icon="notification_add" class="mr-1"></v-icon>
        Enable Push Notifications
      </v-button>
    </div>

    <list boxed heading="Notifications Preferences" class="mt-2">
      <list-item>
        <template slot="left">
          <v-icon icon="notifications_active" />
        </template>
        <div class="pl-2 v-list-item__title --centered">
          Reminders
        </div>
        <template slot="right">
          <v-switch :value="receiveReminders" @input="toggleReminders" />
        </template>
      </list-item>
      <list-item>
        <template slot="left">
          <v-icon icon="forum" />
        </template>
        <div class="pl-2 v-list-item__title --centered">
          Messages
        </div>
        <template slot="right">
          <v-switch
            :value="receiveMessageNotifications"
            @input="toggleMessageNotifications"
          />
        </template>
      </list-item>
      <list-item>
        <template slot="left">
          <v-icon icon="campaign" />
        </template>
        <div class="pl-2 v-list-item__title --centered">
          App Notifications
        </div>
        <template slot="right">
          <v-switch
            :value="receiveBlastNotifications"
            @input="toggleBlastNotification"
          />
        </template>
      </list-item>
      <div class="text-center py-2">
        <template v-if="receiveBlastNotifications || receiveReminders">
          <small>
            You will receive notifications via
            <strong>{{ deliveryType }}</strong>
          </small>
        </template>
        <template v-else>
          <small>You wont receive notifications</small>
        </template>
      </div>
    </list>

    <div class="p-2 mt-2 text-center">
      <v-button
        v-if="isPushEnabled() && isSupportedDevice()"
        class="mt-4"
        small
        ghost
        :is-loading="isLoadingPush"
        @click="disablePush"
      >
        Disable Push Notifications
      </v-button>
    </div>
  </v-page>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex"

import {
  unsubscribeFromPushNotifications,
  requestPermission,
  isSupportedDevice,
  queryPushStatus
} from "@/lib/push-notification"

import { STAGES } from "@/components/modals/NotificationPermissionTrigger"
import Toolbar from "@/components/Toolbar"
import List from "@/components/List"
import ListItem from "@/components/ListItem"
import vSwitch from "@/components/forms/vSwitch"

export default {
  components: {
    Toolbar,
    List,
    ListItem,
    vSwitch
  },

  data() {
    return {
      isLoading: false,
      isLoadingPush: false,
      pushStatusInterval: null
    }
  },

  mounted() {
    this.loadPage()
  },

  beforeDestroy() {
    this.teardownPage()
  },

  activated() {
    this.setupPage()
  },

  deactivated() {
    this.teardownPage()
  },

  computed: {
    ...mapGetters("auth", ["currentUser"]),
    ...mapState("notifications", [
      "canAsk",
      "receiveBlastNotifications",
      "receiveMessageNotifications",
      "receiveReminders"
    ]),
    ...mapGetters("notifications", ["isPushEnabled"]),

    deliveryType() {
      return this.isPushEnabled() ? "Push Notification" : "Email"
    }
  },

  methods: {
    isSupportedDevice,

    ...mapActions("notifications", [
      "fetchNotificationSettings",
      "updateNotificationSettings"
    ]),

    setupPage() {
      queryPushStatus()
      this.pushStatusInterval = setInterval(queryPushStatus, 1500)
    },

    teardownPage() {
      clearInterval(this.pushStatusInterval)
    },

    requestNotifications() {
      this.isLoadingPush = true

      requestPermission()
        .then(() => {
          this.$router.push(STAGES.GRANTED)
        })
        .catch(err => {
          console.log(err)
          this.$router.push(STAGES.DENIED)
        })
        .finally(() => {
          this.isLoadingPush = false
        })
    },

    disablePush() {
      this.isLoadingPush = true
      unsubscribeFromPushNotifications().then(() => {
        this.isLoadingPush = false
      })
    },

    loadPage() {
      this.isLoading = true
      this.fetchNotificationSettings().finally(() => {
        this.isLoading = false
      })
    },

    toggleBlastNotification(value) {
      this.updateSettings(
        value,
        this.receiveReminders,
        this.receiveMessageNotifications
      )
    },

    toggleReminders(value) {
      this.updateSettings(
        this.receiveBlastNotifications,
        value,
        this.receiveMessageNotifications
      )
    },

    toggleMessageNotifications(value) {
      this.updateSettings(
        this.receiveBlastNotifications,
        this.receiveReminders,
        value
      )
    },

    updateSettings(
      receiveBlastNotifications,
      receiveReminders,
      receiveMessageNotifications
    ) {
      this.updateNotificationSettings({
        receiveBlastNotifications,
        receiveMessageNotifications,
        receiveReminders
      })
    }
  }
}
</script>
